import {
    Button,
    createStyles,
    Drawer,
    IconButton,
    makeStyles,
} from "@material-ui/core";
import * as React from "react";

import {
    IFacets,
    Input,
    ISearchFacetConfig,
    LocationSearch,
    RelodeTheme,
    SearchFacets,
    Text,
} from "..";
import { LocationSearchScopeEnum } from "../../";
import { TimesIcon } from "../../assets/icons";
import { LocationOption } from "../location";
import { Location } from "../../domain";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            header: {
                display: "flex",
                justifyContent: "space-between",
            },
            paperAnchorTop: {
                height: "100%",
            },
            filterTitle: {
                margin: theme.spacing(2),
            },
            filters: {
                flexGrow: 1,
            },
            filterButtons: {
                position: "fixed",
                bottom: 0,
                width: "100%",
                backgroundColor: "white",
            },
            keywordInput: {
                margin: theme.spacing(1, 0, 2, 0),
            },
            icon: {
                marginRight: theme.spacing(1),
            },
            radioButton: {
                justifyContent: "space-between",
            },
            filterContent: {
                marginBottom: theme.spacing(10),
                padding: theme.spacing(2),
            },
            showResults: {
                margin: theme.spacing(2),
                width: "90%",
            },
            dateWrapper: {
                marginTop: theme.spacing(2),
                display: "flex",
                flexDirection: "column",
            },
            dateTitle: {
                textTransform: "lowercase",
                color: theme.relode.colors.alternate.main,
                fontWeight: 600,
            },
            dateFilterButton: {
                margin: 0,
                alignSelf: "flex-end",
            },
        }),
    { name: "SearchFiltersMobile" }
);

interface SearchFiltersMobileProps {
    isOpen: boolean;
    onOpenChanged: (isOpen: boolean) => void;
    searchCriteria: any;
    onSearch: (searchText?: string) => void;
    facets?: IFacets;
    facetConfig?: ISearchFacetConfig;
    hasResults: boolean;
    refined?: boolean;
    onLocationFilter?: (option: LocationOption, location: Location) => void;
    selectedLocationName?: string;
    selectedLocationId?: number;
    distanceRadius?: number;
    onDistanceFilter?: (miles: number) => void;
    customFilters?: React.ReactNode;
    isValid?: boolean;
}

export const SearchFiltersMobile = (props: SearchFiltersMobileProps) => {
    const classes = useStyles({});
    const {
        searchCriteria,
        isOpen,
        onOpenChanged,
        onSearch,
        onLocationFilter,
        facets,
        facetConfig,
        hasResults,
        refined,
        selectedLocationId,
        selectedLocationName,
        distanceRadius,
        onDistanceFilter,
        customFilters,
        isValid = true,
    } = props;

    const [currentSearchText, setCurrentSearchText] = React.useState<string>(
        ""
    );

    React.useEffect(() => {
        setCurrentSearchText(searchCriteria.searchText || "");
    }, [searchCriteria]);

    const handleTextSearchEvent = (event: React.ChangeEvent<any>) => {
        setCurrentSearchText(event.target.value);
    };

    const handleTextSearchKeyPress = event => {
        if (event.key === "Enter") {
            onSearch(currentSearchText);
        }
    };

    const handleSearchClick = () => {
        onSearch(currentSearchText);
        onOpenChanged(false);
    };

    const inputPlaceholder = "name, position, etc";

    return (
        <Drawer
            anchor="top"
            open={isOpen}
            onClose={() => onOpenChanged(false)}
            classes={{ paperAnchorTop: classes.paperAnchorTop }}
        >
            <div className={classes.filterContent}>
                <div className={classes.header}>
                    <Text variant="h1" className={classes.filterTitle}>
                        filters
                    </Text>
                    <IconButton onClick={() => onOpenChanged(false)}>
                        <TimesIcon color="primary" />
                    </IconButton>
                </div>
                <div className={classes.filters}>
                    <Input
                        fullWidth
                        className={classes.keywordInput}
                        value={currentSearchText}
                        autoFocus={true}
                        label={inputPlaceholder}
                        onChange={handleTextSearchEvent}
                        onKeyPress={handleTextSearchKeyPress}
                        inputProps={{
                            id: "mobileSearchInput",
                            maxLength: 150,
                        }}
                        hideCharacterLimit={true}
                    />
                </div>
                {onLocationFilter ? (
                    <div className={classes.filters}>
                        <LocationSearch
                            id="jobsSearch"
                            label="city, state or zip"
                            selectedLocation={{
                                locationId: selectedLocationId,
                                locationName: selectedLocationName,
                            }}
                            onSelect={onLocationFilter}
                            onDistanceChange={onDistanceFilter}
                            searchRadiusInMeters={distanceRadius}
                            scope={LocationSearchScopeEnum.ExcludeAny}
                        />
                    </div>
                ) : null}

                {customFilters ? <>{customFilters}</> : null}
                <div>
                    <SearchFacets
                        id="searchFacets"
                        criteria={searchCriteria}
                        facets={facets}
                        facetConfig={facetConfig}
                        hasResults={hasResults}
                        refined={refined}
                        showFiltersTitle={false}
                    />
                </div>
            </div>
            <div className={classes.filterButtons}>
                <Button
                    size="small"
                    color="primary"
                    variant="contained"
                    fullWidth
                    onClick={handleSearchClick}
                    disabled={!isValid}
                    className={classes.showResults}
                >
                    show results
                </Button>
            </div>
        </Drawer>
    );
};
